import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

type UrlValue = string | number | boolean

/**
 * Parses one URL param back into the type of its default. Exported for tests.
 */
export function parseUrlValue<T extends UrlValue>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback
  if (typeof fallback === 'number') {
    const n = Number(raw)
    return (Number.isFinite(n) ? n : fallback) as T
  }
  if (typeof fallback === 'boolean') return (raw === 'true' || raw === '1') as T
  return raw as T
}

/**
 * Page state (page, size, search, view mode, …) kept in the URL query string so lists can be
 * linked, reloaded and navigated with Back. Keys are namespaced with {@code prefix} so several
 * lists can share one page (e.g. child groups and records). Values equal to their default are
 * left out of the URL.
 */
export function useUrlState<T extends Record<string, UrlValue>>(
  prefix: string,
  defaults: T
): [T, (patch: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo(() => {
    const result = { ...defaults }
    for (const key of Object.keys(defaults) as (keyof T & string)[]) {
      result[key] = parseUrlValue(searchParams.get(prefix + key), defaults[key])
    }
    return result
    // defaults is expected to be a stable literal; its JSON is a cheap identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, prefix, JSON.stringify(defaults)])

  const update = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(patch)) {
            const param = prefix + key
            if (value === undefined || value === defaults[key] || value === '') next.delete(param)
            else next.set(param, String(value))
          }
          return next
        },
        { replace: true }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, prefix, JSON.stringify(defaults)]
  )

  return [state, update]
}
